import React from 'react';
import {
  Background,
  BackgroundVariant,
  Controls,
  ReactFlow,
  type EdgeTypes,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ReviewTrace, ReviewVerdict } from '../../../api/structs';
import { ReviewProfileCheckpoint } from '../../../api/structs/reviewProfile';
import useIsDesktop from '../../../hooks/useIsDesktop';
import useReviewProfile from '../../../hooks/useReviewProfile';
import DerivedEdge from '../canvas/edges/DerivedEdge';
import { DERIVED_EDGE_TYPE, REVIEW_EDGE_COLORS, styleEdges } from '../canvas/edgeStyles';
import { ReviewerNode } from './graphTypes';
import { reviewerNodeTypes } from './nodes';
import { profileToGraph } from './profileToGraph';
import ReviewerDetail from './ReviewerDetail';
import ReviewerStageList from './ReviewerStageList';
import { detailMuted } from './reviewerDetailStyles';

/** What each edge colour means, in the words the nodes use. */
const LEGEND: { kind: string; label: string }[] = [
  { kind: 'review_text', label: 'words' },
  { kind: 'review_image', label: 'pictures' },
  { kind: 'review_check', label: 'criteria' },
  { kind: 'review_pass', label: 'pass' },
  { kind: 'review_uncertain', label: 'uncertain' },
  { kind: 'review_fail', label: 'fail' },
];

export interface ReviewerGraphProps {
  /** The template the profile is read from, and whose corrections are listed. */
  templateId: string;
  checkpointId: string;
  /** This run's reviewer calls — turns the capability picture into a record. */
  traces?: ReviewTrace[];
  /** This checkpoint's verdicts, whose metadata says which levers really fired. */
  verdicts?: ReviewVerdict[];
  /** Shorter canvas, no legend, no side column — for embedding in a panel. */
  compact?: boolean;
  /** Whether to render the detail column. Defaults to true unless `compact`. */
  showDetail?: boolean;
  /**
   * A checkpoint to draw INSTEAD of fetching the profile — the editor hands in
   * the one it re-derives from unsaved edits, so the picture reflects the form
   * immediately rather than only after Save.
   */
  checkpoint?: ReviewProfileCheckpoint;
  /**
   * The resolved review mode. Read from the fetched profile when it is not
   * given; a caller supplying `checkpoint` has no profile to read it from and
   * should pass the mode it resolved.
   */
  mode?: string;
}

/**
 * The reviewer, as a picture: what flows into the judge (one node per source,
 * wired to the very `{{slot}}` it fills), the judge with its tiers and gates,
 * its verdict, and what it may then do — plus, beside it, the quiet column that
 * says what those things actually contain.
 *
 * READ-ONLY by construction. Nodes are not draggable and every edge is derived,
 * so the picture always draws the same shape for the same configuration; it is
 * a view of a configuration, not a diagram anyone owns. Capabilities are edited
 * where they live (the checkpoint's review block, Settings → Reviewer).
 */
const ReviewerGraph: React.FC<ReviewerGraphProps> = ({
  templateId,
  checkpointId,
  traces,
  verdicts,
  compact,
  showDetail,
  checkpoint,
  mode,
}) => {
  // A supplied checkpoint is the whole answer: fetching one behind it would
  // race the caller's unsaved edits and could only ever draw a staler picture.
  const { profile, error } = useReviewProfile(checkpoint ? undefined : templateId);
  const isDesktop = useIsDesktop();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  // Compact only. React Flow's fit-view control fits the graph to the EMBEDDED
  // frame, which is the complaint it was mistaken for an answer to: seventeen
  // sources fitted into 256px are still seventeen sources in 256px. This opens
  // the same picture at the size it was drawn for.
  const [fullscreen, setFullscreen] = React.useState(false);

  // Escape closes THIS overlay and stops there — the capture-phase pattern
  // ReviewerPromptSection and the guidance fullscreen both use, so a keypress
  // does not also shut the checkpoint panel or the canvas behind it.
  React.useEffect(() => {
    if (!fullscreen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      event.stopImmediatePropagation();
      setFullscreen(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [fullscreen]);

  const resolved =
    checkpoint ||
    profile?.checkpoints.find((candidate) => candidate.checkpoint_id === checkpointId) ||
    null;
  const resolvedMode = mode ?? profile?.mode ?? '';

  const { nodes, edges } = React.useMemo(
    () =>
      resolved
        ? profileToGraph(resolved, resolvedMode, traces, verdicts)
        : { nodes: [] as ReviewerNode[], edges: [] },
    [resolved, resolvedMode, traces, verdicts]
  );

  const displayNodes = React.useMemo(
    () => nodes.map((node) => ({ ...node, selected: node.id === selectedId, draggable: false })),
    [nodes, selectedId]
  );
  const displayEdges = React.useMemo(() => styleEdges(edges), [edges]);
  // Every reviewer edge is derived, so this is the only edge type it can need.
  const edgeTypes = React.useMemo<EdgeTypes>(
    () => ({ [DERIVED_EDGE_TYPE]: DerivedEdge as unknown as EdgeTypes[string] }),
    []
  );
  const nodeTypes = React.useMemo(
    () => reviewerNodeTypes as Record<string, React.ComponentType<NodeProps>>,
    []
  );

  const withDetail = showDetail ?? !compact;
  const selectedNode = nodes.find((node) => node.id === selectedId) || null;

  if (error) {
    return (
      <p className="font-mono text-[11px] text-red-300" data-testid="reviewer-graph-error">
        {error}
      </p>
    );
  }
  if (!resolved) {
    // No profile yet means the read is in flight or about to be — the effect
    // that starts it runs after this first render, so "missing" would be a lie
    // for one frame if it were the only other answer.
    return !profile ? (
      <p className={detailMuted} data-testid="reviewer-graph-loading">
        Reading the reviewer…
      </p>
    ) : (
      <p className={detailMuted} data-testid="reviewer-graph-missing">
        {`This template has no checkpoint “${checkpointId}”.`}
      </p>
    );
  }

  const picture = isDesktop ? (
    <div
      className={`w-full border border-[var(--border-strong)] bg-[var(--surface-2)] ${
        compact ? 'h-64' : 'h-[540px]'
      }`}
      data-testid="reviewer-graph-canvas"
    >
      <ReactFlow
        nodes={displayNodes}
        edges={displayEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={(_event, node) => setSelectedId(node.id)}
        onPaneClick={() => setSelectedId(null)}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        fitView
        minZoom={0.15}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
        preventScrolling={false}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="var(--border-subtle)"
        />
        {/* A seventeen-source graph fits the frame only zoomed well out, so the
            zoom/fit affordance is not optional here. No interactive lock
            button: nothing on this canvas is editable to begin with. */}
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  ) : (
    <ReviewerStageList
      nodes={nodes}
      selectedId={selectedId}
      onSelect={(nodeId) => setSelectedId((current) => (current === nodeId ? null : nodeId))}
    />
  );

  return (
    <div
      className="flex w-full flex-col gap-3 md:flex-row"
      data-testid="reviewer-graph"
      data-compact={compact ? 'true' : undefined}
    >
      <div className="min-w-0 flex-1 space-y-1.5">
        {compact ? (
          <div className="relative">
            {picture}
            <button
              type="button"
              className="btn btn-sm btn-ghost absolute right-1 top-1 z-10 !px-1.5 !py-0 font-mono text-[10px]"
              data-testid="reviewer-graph-fullscreen"
              title="Open the reviewer full screen"
              aria-label="Open the reviewer full screen"
              onClick={() => setFullscreen(true)}
            >
              ⛶
            </button>
          </div>
        ) : (
          picture
        )}
        {!compact && (
          <div className="flex flex-wrap gap-2" data-testid="reviewer-graph-legend">
            {LEGEND.map((entry) => (
              <span key={entry.kind} className={`flex items-center gap-1 ${detailMuted}`}>
                <span
                  aria-hidden
                  className="inline-block h-0.5 w-4"
                  style={{ background: REVIEW_EDGE_COLORS[entry.kind] }}
                />
                {entry.label}
              </span>
            ))}
          </div>
        )}
      </div>

      {withDetail && (
        <div className="min-w-0 md:w-80 md:flex-shrink-0">
          <ReviewerDetail
            checkpoint={resolved}
            templateId={templateId}
            selectedNode={selectedNode}
          />
        </div>
      )}

      {fullscreen && (
        <div
          className="fixed inset-0 z-[230] flex flex-col bg-black/95 p-4"
          data-testid="reviewer-graph-fullscreen-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="The reviewer"
        >
          <div className="flex items-center gap-2 pb-2">
            <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-[var(--app-text)]">
              The reviewer
            </h4>
            <span className="truncate font-mono text-[11px] text-[var(--app-text-muted)]">
              {checkpointId}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-ghost ml-auto font-mono text-[10px]"
              data-testid="reviewer-graph-fullscreen-exit"
              onClick={() => setFullscreen(false)}
            >
              ⊠ Close
            </button>
          </div>
          <div className="flex-1 overflow-auto">
            {/* The SAME picture, full size, with its detail column. The
                checkpoint already resolved here is handed down explicitly, so
                the full-size copy draws the caller's unsaved edits and never
                pays for a second profile read. */}
            <ReviewerGraph
              templateId={templateId}
              checkpointId={checkpointId}
              traces={traces}
              verdicts={verdicts}
              checkpoint={resolved}
              mode={resolvedMode}
              showDetail
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewerGraph;
